import { Component, computed, effect, forwardRef, inject, input, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { IconModule } from '@insolution/core-data-access';
import {
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbDatepickerModule,
  NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DateFnsDatepickerI18nService } from './formatters/date-fns-datepicker-i18n.service';
import { I18nDateParserFormatter } from './formatters/i18n-date-parser-formatter';
import { getDateYearsFromNow } from '@insolution/core-utils';

@Component({
  selector: 'lib-multi-language-datepicker',
  imports: [NgbDatepickerModule, FormsModule, IconModule, TranslatePipe],
  templateUrl: './multi-language-datepicker.html',
  styleUrl: './multi-language-datepicker.scss',
  providers: [
    { provide: NgbDateParserFormatter, useClass: I18nDateParserFormatter },
    { provide: NgbDatepickerI18n, useClass: DateFnsDatepickerI18nService },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiLanguageDatepicker),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MultiLanguageDatepicker),
      multi: true,
    },
  ],
})
export class MultiLanguageDatepicker implements ControlValueAccessor, Validator {
  public required = input<boolean>(false);
  public minDate = input<NgbDateStruct>(getDateYearsFromNow(-100));
  public maxDate = input<NgbDateStruct>(getDateYearsFromNow(100));

  private translateService = inject(TranslateService);
  protected parserFormatter = inject(I18nDateParserFormatter);

  private ngbDatepicker = viewChild<{
    _elRef: { nativeElement: HTMLInputElement };
  }>('d');

  protected date = signal<NgbDateStruct | null>(null);
  private currentFormat = signal<string>(this.parserFormatter['getFormat']());
  protected readonly placeholder = computed<string>(() => this.currentFormat());
  protected readonly formattedDate = computed(() => {
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    const _ = this.currentFormat();
    const d = this.date();

    if (!d) {
      return '';
    }

    if (typeof d === 'string') {
      return d;
    }

    if (d.year.toString().length < 4) {
      const currentValue = this.ngbDatepicker()?._elRef.nativeElement?.value;
      return currentValue || '';
    }

    const formattedDate = this.parserFormatter.format(d);

    return formattedDate;
  });

  private onChange: (value: string | null) => void = () => {
    /* intentionally empty */
  };
  private onTouched: () => void = () => {
    /* intentionally empty */
  };
  private disabled = signal<boolean>(false);
  private initialized = signal<boolean>(false);

  constructor() {
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.currentFormat.set(this.parserFormatter.getFormat()));

    effect(() => {
      const d = this.date();

      untracked(() => {
        if (!this.disabled() && this.initialized()) {
          this.onChange(this.parserFormatter.ngbDateStructToISO(d));
        }
      });
    });
  }

  writeValue(obj: string | null): void {
    const date = this.parserFormatter.parseISO(obj ?? '');
    if (this.date() !== date) {
      this.date.set(date);
    }

    if (!this.initialized()) {
      setTimeout(() => {
        this.initialized.set(true);
      });
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  validate(): { [key: string]: any } | null {
    const d = this.date();
    const errors: ValidationErrors = {};

    if (this.required() && !d) {
      errors['required'] = true;
    }
    if (d) {
      const min = this.minDate();
      const max = this.maxDate();
      if (min && compareDates(d, min) < 0) {
        errors['minDate'] = true;
      }
      if (max && compareDates(d, max) > 0) {
        errors['maxDate'] = true;
      }
    }
    // add more validation if needed, e.g. check if date is valid
    return Object.keys(errors).length > 0 ? errors : null;
  }

  protected onDateInputChanged(value: string): void {
    const date = this.parserFormatter.parse(value);
    if (this.date() !== date) {
      this.date.set(date);
    }
  }

  protected onBlur(): void {
    this.onTouched();
  }
}

// Helper function to compare NgbDateStructs
function compareDates(a: NgbDateStruct, b: NgbDateStruct): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}
